
import NumberInput from '@components/new-styleguide/form/NumberInput.vue';
import addressFormat from '@util/addressFormat';
import { formatTripOdometer } from '@util/odometerService';
import { objectToQueryParam } from '@util/parseQueryUtils';
import Vue from 'vue';
import { Address, Location, Trip, TripInfo, TripMutation, Zone } from '@clients/myTrips';
import { Component, Prop, Watch } from 'vue-property-decorator';
import TripDetailDataLine from '@components/trip-detail/trip-data/TripDetailDataLine.vue';
import AddressInputField from '@components/new-styleguide/form/AddressInputField.vue';
import LocationAddress from '@/models/LocationAddress';
import deepCopy from '@util/copy';
import { formatInputDate, formatTime, formatTripDateTime } from '@util/dates-and-times';
import { featureToAddress, featureToLocation } from '@util/mapbox';
import { namespace } from 'vuex-class';

const GeographicsModule = namespace('Geographics');
const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');

export type Point = 'startInfo' | 'endInfo';

@Component({
  name: 'TripDataLocationTemplate',
  components: {
    NumberInput,
    AddressInputField,
    TripDetailDataLine
  }
})

export default class TripDataLocationTemplate extends Vue {
  $refs!: {
    address: AddressInputField,
    date: HTMLFormElement,
    odometer: NumberInput
  }

  @Prop({ default: false })
  private addMode: boolean;

  @Prop()
  private trip: Trip;

  @Prop()
  private tripMutation: TripMutation;

  @Prop({ required: true })
  private point: Point;

  @Prop()
  private minTimestamp: Date;

  @Prop()
  private maxTimestamp: Date;

  @Prop()
  private minOdometer: number; //m

  @Prop()
  private maxOdometer: number; //m

  @Prop({ default: false })
  private loading: boolean;

  @GeographicsModule.Action('fetchPlaceNames')
  private fetchPlaceNames;

  @TriplogModule.Action('fetchLatestTrip')
  private fetchLatestTrip;

  @SettingsModule.Getter('getZones')
  private zones: Zone[];

  private inputDate: string = null;
  private inputTime: string = null;
  private inputOdometer: number = null;

  private latestTripId: number = null;

  async created() {
    this.updateTripMutationDateTime();
    if (!this.addMode) {
      const latestTrip = await this.fetchLatestTrip({ vehicleId: parseInt(this.$route.query.vehicle as string) });
      if (latestTrip) {
        this.latestTripId = latestTrip.id;
      }
    }
  }

  @Watch('tripMutation')
  onTripMutationChanged() {
    this.updateTripMutationDateTime();
  }

  get addZoneButton() {
    return this.trip && this.trip[this.point] && !this.trip[this.point].zone;
  }

  get odometerValue() {
    return this.inputOdometer !== null && this.inputOdometer !== undefined ? this.inputOdometer / 1000 : null;
  }

  get zoneRoute() {
    if (!this.addMode && this.trip && this.trip[this.point] && this.trip[this.point].address) {
      return this.addZoneRoute(this.tripInfo);
    }
    return null;
  }

  get address() {
    if (!this.addMode && this.trip && this.trip[this.point] && this.trip[this.point].address) {
      return this.getAddress(this.trip[this.point].address);
    }
    return !this.loading && this.isLatestTrip ? null : '';
  }

  get zonePrefix() {
    if (!this.editMode && this.trip && !this.addMode && this.trip[this.point] && this.trip[this.point].zone) {
      return this.trip[this.point].zone.name;
    }
  }

  get timestamp() {
    if (!this.addMode && this.trip && this.trip[this.point] && this.trip[this.point].timestamp) {
      return formatTripDateTime(this.trip[this.point].timestamp);
    }
    return !this.loading && this.isLatestTrip ? null : '';
  }

  get odometer() {
    if (!this.addMode && this.trip && this.trip[this.point] && this.trip[this.point].odometer) {
      return formatTripOdometer(this.trip[this.point].odometer) + ' km';
    }
    return !this.loading && this.isLatestTrip ? null : '';
  }

  get buttonColor() {
    return this.point === 'startInfo' ? 'green' : 'blue';
  }

  get namePrefix() {
    return this.point === 'startInfo' ? 'Start-' : 'End-';
  }

  get textPrefix() {
    return this.point === 'startInfo' ? 'Abfahrt' : 'Ankunft';
  }

  get buttonLabel() {
    return this.point === 'startInfo' ? 'Start' : 'Ziel';
  }

  get editMode() {
    return !!this.tripMutation;
  }

  get minTime() {
    if (this.inputDate === formatInputDate(this.minTimestamp)) {
      return formatTime(this.minTimestamp);
    }
  }

  get maxTime() {
    if (this.inputDate === formatInputDate(this.maxTimestamp)) {
      return formatTime(this.maxTimestamp);
    }
  }

  get minDate() {
    return formatInputDate(this.minTimestamp);
  }

  get maxDate() {
    return formatInputDate(this.maxTimestamp);
  }

  get tripInfo() {
    return this.editMode ? this.tripMutation[this.point] : this.trip[this.point];
  }

  get isLatestTrip() {
    return !this.addMode && this.trip.id === this.latestTripId;
  }

  public focusField(value: string) {
    if (value === 'address') {
      this.$refs.address.focusField();
    } else if (value === 'date') {
      this.$refs.date.click();
      this.$refs.date.focus();
    } else if (value === 'odometer') {
      this.$refs.odometer.focusField();
    }
  }

  private addZoneRoute(tripLocationInfo: TripInfo) {
    const locationAddress: LocationAddress = {
      address: { ...tripLocationInfo.address },
      location: { ...tripLocationInfo.location }
    }

    return {
      name: 'zonesAdd',
      query: {
        tripId: this.trip.id,
        locationAddress: objectToQueryParam(locationAddress)
      }
    }
  }

  private getAddress(address: Address) {
    return addressFormat(address);
  }

  private emitEdit(value: string) {
    this.$emit('toggleEditMode', value);
  }

  private handleDateChange() {
    if (!this.inputDate) {
      if (this.trip && this.trip[this.point] && this.trip[this.point].timestamp) {
        this.inputDate = formatInputDate(this.trip[this.point].timestamp);
      } else {
        this.inputDate = this.minDate;
      }
      if (!this.inputTime) {
        if (this.trip && this.trip[this.point] && this.trip[this.point].timestamp) {
          this.inputTime = formatTime(this.trip[this.point].timestamp);
        } else {
          this.inputTime = this.minTime;
        }
      }
    }
    this.validateTime();
    this.emitDateTime();
  }

  private handleTimeChange() {
    if (!this.inputTime) {
      if (this.trip && this.trip[this.point] && this.trip[this.point].timestamp) {
        this.inputTime = formatTime(this.trip[this.point].timestamp);
      } else {
        this.inputTime = this.minTime;
      }
    }
    this.validateTime();
    this.emitDateTime();
  }

  private handleDateInput(value: string) {
    if ((!this.minDate || value >= this.minDate) && (!this.maxDate || value <= this.maxDate)) {
      this.inputDate = value;
    }
  }

  private handleTimeInput(value: string) {
    if ((!this.minTime || value >= this.minTime) && (!this.maxTime || value <= this.maxTime)) {
      this.inputTime = value;
    }
  }

  private validateTime() {
    if (this.minTime && this.minTime > this.inputTime) {
      this.inputTime = this.minTime;
    }
    if (this.maxTime && this.maxTime < this.inputTime) {
      this.inputTime = this.maxTime;
    }
  }

  private emitDateTime() {
    const copy = deepCopy(this.tripMutation);
    copy[this.point].timestamp = new Date(this.inputDate + 'T' + this.inputTime).toISOString();
    this.$emit('input', copy);
  }

  private handleOdometerInput(odometer: number) {
    this.inputOdometer = odometer * 1000;
  }

  private handleOdometerChange() {
    const copy = deepCopy(this.tripMutation);
    copy[this.point].odometer = this.inputOdometer;
    this.$emit('input', copy);
  }

  private handleAddressChange(la: LocationAddress) {
    const copy = deepCopy(this.tripMutation);
    copy[this.point] = {
      ...copy[this.point],
      ...la
    };
    this.$emit('input', copy);
  }

  private async handleLocationButton(location: Location) {
    const copy = deepCopy(this.tripMutation);
    copy[this.point] = {
      ...copy[this.point],
      ...await this.changeAddress(location)
    };
    this.$emit('input', copy);
  }

  private async changeAddress(location: Location) {
    const results = await this.fetchPlaceNames({ longitude: location.longitude, latitude: location.latitude,  poi: true })

    if (results && results.features && results.features[0]) {
      return {
        address: featureToAddress(results.features[0]),
        location: featureToLocation(results.features[0])
      };
    }
  }

  private updateTripMutationDateTime() {
    this.inputTime = null;
    this.inputDate = null;
    this.inputOdometer = null;
    if (this.tripMutation && this.tripMutation[this.point] && this.tripMutation[this.point].timestamp) {
      this.inputDate = formatInputDate(this.tripMutation[this.point].timestamp);
      this.inputTime = formatTime(this.tripMutation[this.point].timestamp);
    }
    if (this.tripMutation && this.tripMutation[this.point] && (this.tripMutation[this.point].odometer || this.tripMutation[this.point].odometer === 0)) {
      this.inputOdometer = this.tripMutation[this.point].odometer;
    }
  }
}
